import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { BaseCommand, ICommand } from '../../commands/ICommand';
import { ToolComponentFactory } from '../components/ToolComponents';
import { Trash2 } from 'lucide-react';

// Generic delete command
export class DeleteItemsCommand extends BaseCommand {
  protected deletedItems: any[];
  protected restoreItems: (items: any[]) => void;
  protected deleteItems: (ids: string[]) => void;
  
  constructor(
    deletedItems: any[],
    deleteItems: (ids: string[]) => void,
    restoreItems: (items: any[]) => void,
    description?: string
  ) {
    super('Delete Items', description || `Delete ${deletedItems.length} item(s)`);
    this.deletedItems = [...deletedItems];
    this.deleteItems = deleteItems;
    this.restoreItems = restoreItems;
  }
  
  execute(): void {
    this.deleteItems(this.deletedItems.map(item => 
      typeof item === 'string' ? item : item.id
    ));
  }
  
  undo(): void {
    this.restoreItems(this.deletedItems);
  }
}

// Base delete tool
export abstract class DeleteTool extends BaseTool {
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Delete',
      icon: '🗑️',
      category: 'edit',
      description: 'Delete selected items',
      version: '1.0.0',
      
      requiresSelection: true,
      minSelection: 1,
      
      ui: {
        toolbar: true,
        contextMenu: true,
        propertyPanel: false
      },
      
      capabilities: {
        modifiesShapes: false,
        createsShapes: false,
        deletesShapes: true,
        modifiesConnectors: false,
        createsConnectors: false,
        deletesConnectors: true,
        requiresUserInput: false,
        supportsMultiSelect: true,
        supportsBatchOperations: true
      },
      
      shortcuts: [{
        key: 'Delete',
        modifiers: [],
        description: 'Delete selected items'
      }, {
        key: 'Backspace',
        modifiers: [],
        description: 'Delete selected items'
      }]
    };
  }
  
  canExecute(context: IToolContext): boolean {
    return context.selection.size > 0;
  }
  
  // Abstract methods
  abstract getItemsToDelete(): any[];
  abstract deleteItems(ids: string[]): void;
  abstract restoreItems(items: any[]): void;
  
  execute(): ICommand | null {
    if (!this.context) return null;
    
    const itemsToDelete = this.getItemsToDelete();
    if (itemsToDelete.length === 0) return null;
    
    return new DeleteItemsCommand(
      itemsToDelete,
      (ids) => this.deleteItems(ids),
      (items) => this.restoreItems(items)
    );
  }
  
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const selectedCount = this.context?.selection.size || 0;
      const isDisabled = selectedCount === 0;
      
      return ToolComponentFactory.createButton({
        icon: Trash2,
        label: `Delete${selectedCount > 0 ? ` (${selectedCount})` : ''}`,
        onClick: () => onExecute({}),
        disabled: isDisabled,
        variant: 'outline' // Changed from 'destructive' to maintain neutral theme
      });
    };
  }
}